import React from 'react';
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  CalendarPlus,
  CheckCircle,
  ChevronDown,
  LayoutDashboard,
  Landmark,
  LogOut,
  Menu,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { ViewState, Language } from '../types';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { isoWeek } from '@/features/dashboard/api';
import { cn } from '@/lib/utils';

interface SidebarUser {
  id: string;
  name: string;
  role: 'cleaner' | 'supervisor' | 'admin';
  email: string;
  initials: string;
}

interface SidebarProps {
  currentView: ViewState;
  onChangeView: (view: ViewState) => void;
  language: Language;
  setLanguage: (lang: Language) => void;
  user: SidebarUser | null;
  onLogout: () => void;
  onEmergencyLogout: () => void;
}

const copy = {
  en: {
    dashboard: 'Dashboard',
    myJobs: 'My jobs',
    brfs: 'BRFs',
    calendar: 'General Calendar',
    extras: 'Extra blocks',
    team: 'Team',
    approvals: 'Approvals',
    qc: 'Quality Control',
    economy: 'Economy',
    notifications: 'Notifications',
    navigation: 'NAVIGATION',
    resetLabel: 'Workspace',
    logout: 'Sign Out',
    emergencyLogout: 'Emergency Logout',
    systemsOk: 'ALL SYSTEMS GO',
  },
  sv: {
    dashboard: 'Översikt',
    myJobs: 'Mina jobb',
    brfs: 'BRF:er',
    calendar: 'Generell kalender',
    extras: 'Extrastäd',
    team: 'Team',
    approvals: 'Godkänn',
    qc: 'Kvalitetskontroll',
    economy: 'Ekonomi',
    notifications: 'Notiser',
    navigation: 'NAVIGATION',
    resetLabel: 'Arbetsyta',
    logout: 'Logga ut',
    emergencyLogout: 'Nödutloggning',
    systemsOk: 'ALLA SYSTEM OK',
  },
} as const;

export const Sidebar: React.FC<SidebarProps> = ({
  currentView,
  onChangeView,
  language,
  setLanguage,
  user,
  onLogout,
  onEmergencyLogout,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const text = copy[language];
  const weekNum = isoWeek(new Date());

  const navItems =
    user?.role === 'cleaner'
      ? [{ id: 'dashboard' as ViewState, label: text.myJobs, icon: LayoutDashboard }]
      : user?.role === 'admin'
        ? [
            { id: 'dashboard' as ViewState, label: text.dashboard, icon: LayoutDashboard },
            { id: 'brfs' as ViewState, label: text.brfs, icon: Building2 },
            { id: 'calendar' as ViewState, label: text.calendar, icon: CalendarDays },
            { id: 'approvals' as ViewState, label: text.approvals, icon: CheckCircle },
            { id: 'qc' as ViewState, label: text.qc, icon: ShieldCheck },
            { id: 'economy' as ViewState, label: text.economy, icon: Landmark },
            { id: 'team' as ViewState, label: text.team, icon: Users },
          ]
        : [
            { id: 'dashboard' as ViewState, label: text.dashboard, icon: LayoutDashboard },
            { id: 'brfs' as ViewState, label: text.brfs, icon: Building2 },
            { id: 'calendar' as ViewState, label: text.calendar, icon: CalendarDays },
            { id: 'extras' as ViewState, label: text.extras, icon: CalendarPlus },
            { id: 'approvals' as ViewState, label: text.approvals, icon: CheckCircle },
            { id: 'qc' as ViewState, label: text.qc, icon: ShieldCheck },
          ];

  const navButton = (
    item: { id: ViewState; label: string; icon: typeof LayoutDashboard },
    isMobile = false,
  ) => {
    const Icon = item.icon;
    const active = currentView === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => {
          onChangeView(item.id);
          setMobileMenuOpen(false);
        }}
        className={cn(
          'w-full flex items-center gap-2.5 px-2 rounded transition-colors duration-150 group',
          !active && 'hover:bg-[#C9A98A]/10',
        )}
        style={{
          height: isMobile ? 44 : 36,
          background: active ? 'rgba(201,169,138,0.08)' : undefined,
          borderLeft: active ? '2px solid #C9A98A' : '2px solid transparent',
        }}
      >
        <Icon
          size={isMobile ? 18 : 16}
          className={cn(
            'transition-colors duration-150',
            active ? 'text-[#C9A98A]' : 'text-[#8FA89B] group-hover:text-[#C9A98A]',
          )}
        />
        <span
          className={cn(
            'font-mono transition-colors duration-150',
            active
              ? 'text-[#C9A98A] font-medium'
              : 'text-[#C4C3B7] group-hover:text-[#F0EEE6]',
          )}
          style={{ fontSize: isMobile ? 14 : 13 }}
        >
          {item.label}
        </span>
      </button>
    );
  };

  // Language toggle — two small square mono buttons
  const languageToggle = (
    <div className="flex gap-1">
      {(['sv', 'en'] as const).map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => setLanguage(lang)}
          className="flex-1 rounded transition-colors duration-150"
          style={{
            height: 28,
            fontFamily: 'ui-monospace, monospace',
            fontSize: 11,
            letterSpacing: '0.1em',
            border:
              language === lang
                ? '1px solid rgba(201,169,138,0.6)'
                : '1px solid rgba(196,195,183,0.1)',
            background:
              language === lang
                ? 'rgba(201,169,138,0.1)'
                : 'transparent',
            color: language === lang ? '#C9A98A' : '#607C6E',
          }}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );

  // System status line
  const systemStatus = (
    <div
      className="flex items-center gap-1.5 px-1 py-1.5"
      style={{
        borderTop: '1px solid rgba(196,195,183,0.07)',
      }}
    >
      <div
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{
          background: '#4ADE80',
          animation: 'cmd-pulse 2.4s ease-in-out infinite',
        }}
      />
      <span
        className="flex-1"
        style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: 9,
          letterSpacing: '0.12em',
          color: '#4ADE80',
          textTransform: 'uppercase',
        }}
      >
        {text.systemsOk}
      </span>
      <span
        style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: 9,
          color: '#607C6E',
        }}
      >
        V.{weekNum}
      </span>
    </div>
  );

  const userMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center gap-2.5 px-2 py-2 rounded transition-colors duration-150"
          style={{
            background: 'rgba(196,195,183,0.05)',
            border: '1px solid rgba(196,195,183,0.08)',
          }}
        >
          <Avatar className="h-7 w-7 shrink-0">
            <AvatarFallback
              style={{
                background: 'rgba(201,169,138,0.2)',
                color: '#C9A98A',
                fontFamily: 'ui-monospace, monospace',
                fontSize: 11,
              }}
            >
              {user?.initials ?? 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 text-left min-w-0">
            <p
              className="truncate font-mono"
              style={{ fontSize: 12, color: '#C4C3B7' }}
            >
              {user?.name ?? 'User'}
            </p>
            <p
              className="truncate font-mono"
              style={{ fontSize: 9, color: '#607C6E', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              {user?.role ?? 'cleaner'}
            </p>
          </div>
          <ChevronDown
            size={12}
            style={{ color: '#607C6E', flexShrink: 0 }}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled
          className="opacity-100 focus:bg-transparent"
        >
          <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
            {user?.role ?? 'cleaner'}
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onLogout}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          {text.logout}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onEmergencyLogout}>
          <AlertTriangle className="mr-2 h-4 w-4" />
          {text.emergencyLogout}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const sidebarContent = (isMobile = false) => (
    <>
      {/* Logo */}
      <div
        className="p-3 shrink-0"
        style={{ borderBottom: '1px solid rgba(196,195,183,0.08)' }}
      >
        <div className="bg-white rounded p-1.5">
          <img
            src="/optimal_long.png"
            alt="Optimal Trappstädning"
            className="w-full h-auto"
          />
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
        <p
          className="px-2 py-1.5"
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: 9,
            letterSpacing: '0.18em',
            color: '#607C6E',
            textTransform: 'uppercase',
          }}
        >
          {text.navigation}
        </p>
        {navItems.map((item) => (
          <React.Fragment key={item.id}>
            {navButton(item, isMobile)}
          </React.Fragment>
        ))}
      </nav>

      {/* Footer */}
      <div
        className="p-2 space-y-2 shrink-0"
        style={{ borderTop: '1px solid rgba(196,195,183,0.08)' }}
      >
        {languageToggle}
        {systemStatus}
        {userMenu}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div
        className="hidden md:flex w-56 flex-col h-full z-20 relative"
        style={{
          background: '#073331',
          borderRight: '1px solid rgba(196,195,183,0.08)',
        }}
      >
        {sidebarContent(false)}
      </div>

      {/* Mobile top bar + sheet */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-50 px-4 py-2 flex items-center justify-between"
        style={{
          background: '#073331',
          borderBottom: '1px solid rgba(196,195,183,0.08)',
        }}
      >
        <img
          src="/optimal_long.png"
          alt="Optimal Trappstädning"
          className="h-8 w-auto bg-white rounded px-1"
        />

        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="p-1.5 rounded transition-colors"
              style={{ color: '#C4C3B7' }}
            >
              <Menu size={20} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-72 p-0 flex flex-col"
            style={{
              background: '#073331',
              border: 'none',
              borderRight: '1px solid rgba(196,195,183,0.08)',
            }}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{text.navigation}</SheetTitle>
            </SheetHeader>
            {sidebarContent(true)}
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};
